import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	EventEmitter,
} from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { ToastService } from "angular-toastify";
import { Subscription, combineLatest } from "rxjs";
import { ILessonActivities, ILessonActivity } from "src/app/shared/models/activities.model";
import { IActivityDescription } from "src/app/shared/models/activity-description.model";
import {
	Adventure,
	AdventureContent,
	AdventureReward,
	AdventureStar,
	CHEST_RARITY,
	STAR_VALUES,
	AdventureStars,
	AdventureLesson,
	ActivityContent,
} from "src/app/shared/models/adventure.model";
import { ActivityDescriptionService } from "src/app/shared/services/activity-description.service";
import { FirestoreService } from "src/app/shared/services/firestore.service";

interface ActivityWithLesson extends ILessonActivity {
	lessonName: string;
	lessonId: string;
}

interface StarConfig {
	life: number;
	activities: any[];
	rewards: any[];
}

@Component({
	selector: "app-adventure-custom-config",
	templateUrl: "./adventure-custom-config.component.html",
	styleUrls: ["./adventure-custom-config.component.scss"],
})
export class AdventureCustomConfigComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	@ViewChild("activitiesPaginator") activitiesPaginator!: MatPaginator;

	@Input() adventure!: Adventure;
	@Input() selectedLessons: AdventureLesson[] = [];
	@Output() configSaved = new EventEmitter<Adventure>();
	@Output() configCancelled = new EventEmitter<void>();

	readonly ChestRarity = CHEST_RARITY;
	readonly StarValues = STAR_VALUES;
	private subscriptions = new Subscription();

	currentStar = 0;
	totalStars: AdventureStars = 1;
	starConfigs: StarConfig[] = [];
	allActivities: ActivityWithLesson[] = [];
	filteredActivitiesDataSource!: MatTableDataSource<ActivityWithLesson>;
	activitiesColumn = [
		"description",
		"text",
		"skill",
		"game",
		"lesson",
		"pick",
	];
	activityDescriptions: IActivityDescription[] = [];
	loading = true;

	// Novos estados para seleção de lições para atividades
	showLessonPicker = true;
	selectedActivityLessons: string[] = [];

	lessonActivitiesMap: { [lessonId: string]: ActivityWithLesson[] } = {};

	constructor(
		private toastService: ToastService,
		private firestore: FirestoreService,
		private descriptionService: ActivityDescriptionService
	) {}

	ngOnInit() {
		this.initializeFromAdventure();
		this.loadData();
	}

	ngOnDestroy(): void {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	ngAfterViewInit(): void {
		if (this.filteredActivitiesDataSource && this.activitiesPaginator) {
			this.filteredActivitiesDataSource.paginator =
				this.activitiesPaginator;
		}
	}

	private initializeFromAdventure() {
		if (
			this.adventure.recommendation.type === "Custom" &&
			this.adventure.recommendation.starConfig
		) {
			this.totalStars = this.adventure.recommendation.stars;
			this.initializeStarConfigs();
			this.loadCustomRecommendationData();
		} else {
			this.initializeStarConfigs();
		}
	}

	private initializeStarConfigs() {
		this.starConfigs = [];
		for (let i = 0; i < this.totalStars; i++) {
			this.starConfigs.push({
				life: 3,
				activities: [],
				rewards: [],
			});
		}
	}

	private loadCustomRecommendationData() {
		if (
			this.adventure.recommendation.type === "Custom" &&
			this.adventure.recommendation.starConfig
		) {
			this.adventure.recommendation.starConfig.forEach(
				(starConfig, index) => {
					if (this.starConfigs[index]) {
						this.starConfigs[index] = {
							life: starConfig.life,
							activities: starConfig.content.map((content) => ({
								type: content.type,
								uid: content.content.uid,
								lessonId: content.content.lessonId,
							})),
							rewards: Array(starConfig.content.length)
								.fill({ type: "", amount: 1 })
								.map((reward, rewardIndex) => {
									const existingReward =
										starConfig.rewards.find(
											(r) => r.index === rewardIndex
										);
									return existingReward
										? {
												type: existingReward.type,
												amount: existingReward.amount,
										  }
										: { type: "", amount: 1 };
								}),
						};
					}
				}
			);
		}
	}

	private loadData() {
		this.loading = true;
		const descriptionsSub =
			this.descriptionService.getActivityDescriptions();

		const activitiesSubs = this.selectedLessons.map((lesson) =>
			this.firestore.getLessonActivitiesByVersion(
				lesson.lessonId,
				lesson.version
			)
		);

		combineLatest([descriptionsSub, ...activitiesSubs]).subscribe({
			next: ([descriptions, ...lessonActivitiesArr]) => {
				this.activityDescriptions = descriptions;
				this.allActivities = [];

				lessonActivitiesArr.forEach((lesson, index) => {
					const lessonDoc = lesson[0];

					if (!lessonDoc) {
						return;
					}

					const activities = lessonDoc.activities.map((activity) => {
						return {
							...activity,
							description:
								this.getDescriptionName(activity.description) ??
								activity.id,
							text: activity.text || "Vocabulary",
							lessonName: lessonDoc.name || "Unknown Lesson",
							lessonId: lessonDoc.lessonId,
						}
					})
					this.allActivities.push(...activities);
					this.lessonActivitiesMap[lessonDoc.lessonId] = activities;
				});
				this.updateActivitiesTable([]);
			},
			error: (error) => {
				console.error("Error loading activities:", error);
				this.toastService.error("Failed to load activities data");
				this.loading = false;
			},
		});
	}

	private updateActivitiesTable(
		lessonsToLoad: AdventureLesson[]
	) {
		const allActs: ActivityWithLesson[] = [];

		for (const lesson of lessonsToLoad) {
			const acts = this.lessonActivitiesMap[lesson.lessonId] || [];
			allActs.push(...acts);
		}

		this.filteredActivitiesDataSource =
			new MatTableDataSource<ActivityWithLesson>(allActs);
		setTimeout(() => {
			if (this.activitiesPaginator) {
				this.filteredActivitiesDataSource.paginator =
					this.activitiesPaginator;
			}
		});
		this.loading = false;
	}

	openLessonPicker() {
		this.showLessonPicker = true;
	}

	toggleLessonSelection(lessonId: string) {
		const lesson = this.selectedLessons.find(
			(l) => l.lessonId === lessonId
		);
		if (!lesson) return;
		if (this.selectedActivityLessons.includes(lessonId)) {
			this.selectedActivityLessons = this.selectedActivityLessons.filter(
				(id) => id !== lessonId
			);
			this.updateActivitiesTable(
				this.selectedLessons.filter((l) =>
					this.selectedActivityLessons.includes(l.lessonId)
				)
			);
		} else {
			this.selectedActivityLessons = [
				...this.selectedActivityLessons,
				lessonId,
			];
			this.updateActivitiesTable(
				this.selectedLessons.filter((l) =>
					this.selectedActivityLessons.includes(
						l.lessonId
					)
				)
			);
		}
	}

	selectTotalStars(stars: number) {
		const newTotal = stars as AdventureStars;
		if (newTotal > this.totalStars) {
			for (let i = this.totalStars; i < newTotal; i++) {
				this.starConfigs.push({
					life: 3,
					activities: [],
					rewards: [],
				});
			}
		} else if (newTotal < this.totalStars) {
			this.starConfigs.splice(newTotal);
			if (this.currentStar >= newTotal) {
				this.currentStar = newTotal - 1;
			}
		}
		this.totalStars = newTotal;
	}

	goToStar(index: number) {
		if (index >= 0 && index < this.totalStars) {
			this.currentStar = index;
		}
	}

	applyActivityFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.filteredActivitiesDataSource.filter = filterValue
			.trim()
			.toLowerCase();

		if (this.filteredActivitiesDataSource.paginator) {
			this.filteredActivitiesDataSource.paginator.firstPage();
		}
	}

	isActivityPicked(currentStar: number, activity: ActivityWithLesson) {
		return this.starConfigs[currentStar].activities.some(
			(item) => item.uid === activity.id
		);
	}

	addActivityToStar(starIndex: number, activity: ActivityWithLesson) {
		const starConfig = this.starConfigs[starIndex];
		const activityExists = starConfig.activities.some(
			(item) => item.uid === activity.id
		);

		if (!activityExists) {
			starConfig.activities.push({
				type: "Activity",
				uid: activity.id,
				lessonId: activity.lessonId,
			});

			starConfig.rewards.push({
				type: "",
				amount: 1,
			});
		}
	}

	removeActivityFromStar(starIndex: number, activityIndex: number) {
		const starConfig = this.starConfigs[starIndex];
		starConfig.activities.splice(activityIndex, 1);
		starConfig.rewards.splice(activityIndex, 1);
	}

	dropActivity(event: CdkDragDrop<any[]>, starIndex: number) {
		const starConfig = this.starConfigs[starIndex];
		moveItemInArray(
			starConfig.activities,
			event.previousIndex,
			event.currentIndex
		);
	}

	isActivityInvalid(uid: string, lessonId: string) {
		return this.adventure.invalidContent?.includes(uid) || this.selectedLessons.every(lesson => lesson.lessonId !== lessonId);
	}

	getActivityDescription(uid: string): string {
		const activity = this.allActivities.find(
			(activity) => activity.id === uid
		);
		return activity ? activity.description : "";
	}

	getActivityText(uid: string): string {
		const activity = this.allActivities.find(
			(activity) => activity.id === uid
		);
		return activity ? activity.text : "";
	}

	getActivityLesson(uid: string): string {
		const activity = this.allActivities.find(
			(activity) => activity.id === uid
		);
		return activity ? activity.lessonName : "";
	}

	getActivityGame(uid: string): string {
		const activity = this.allActivities.find(
			(activity) => activity.id === uid
		);
		return activity ? activity.game : "";
	}

	getDescriptionName(description: string) {
		const data = this.activityDescriptions.find(
			(data) => data.description === description
		);
		return data?.name ?? description;
	}

	onRarityChange(
		starIndex: number,
		newRarity: string,
		rewardIndex: number
	): void {
		this.starConfigs[starIndex].rewards.forEach((reward, idx) => {
			if (idx !== rewardIndex && reward.type === newRarity) {
				reward.type = "";
			}
		});
		this.starConfigs[starIndex].rewards[rewardIndex].type = newRarity;
	}

	onAmountChange(
		starIndex: number,
		newAmount: number,
		rewardIndex: number
	): void {
		this.starConfigs[starIndex].rewards[rewardIndex].amount = 1;
	}

	getRarityIcon(rarity: string): string {
		switch (rarity) {
			case "common":
				return "assets/chests/chest_common.svg";
			case "rare":
				return "assets/chests/chest_rare.svg";
			case "epic":
				return "assets/chests/chest_epic.svg";
			default:
				return "";
		}
	}

	saveConfig() {
		let canSave = true;

		// Não permitir salvar se houver atividades inválidas
		if (this.hasInvalidActivities()) {
			this.toastService.error('Existem atividades inválidas selecionadas. Remova-as antes de salvar.');
			return;
		}

		for (let i = 0; i < this.starConfigs.length; i++) {
			const config = this.starConfigs[i];

			if (!config.life || config.life < 1) {
				this.toastService.error(
					`Star ${i + 1} life must be at least 1.`
				);
				canSave = false;
			}

			if (!config.activities || config.activities.length === 0) {
				this.toastService.error(
					`Star ${i + 1} must have at least one activity.`
				);
				canSave = false;
			}

			if (
				!config.rewards ||
				config.rewards.filter((r) => r.type).length === 0
			) {
				this.toastService.error(
					`Star ${i + 1} must have at least one reward.`
				);
				canSave = false;
			}
		}

		if (!canSave) {
			return;
		}

		const customRecommendation = {
			type: "Custom" as const,
			stars: this.totalStars,
			starConfig: this.starConfigs.map((config, index) => {
				return {
					life: config.life,
					rewards: config.rewards
						.map((reward, rewardIndex) => {
							return {
								type: reward.type,
								index: rewardIndex,
								amount: reward.amount,
							} as AdventureReward;
						})
						.filter((reward) => reward.type),
					content: config.activities.map((activity) => {
						return {
							type: activity.type,
							content: {
								uid: activity.uid,
								lessonId: activity.lessonId,
							} as ActivityContent,
						} as AdventureContent;
					}),
				} as AdventureStar;
			}),
		};

		// Update the adventure data
		this.adventure.recommendation = customRecommendation;

		this.toastService.success(
			"Custom recommendation configuration saved successfully."
		);
		this.configSaved.emit(this.adventure);
	}

	// Novo método para checar atividades inválidas
	hasInvalidActivities(): boolean {
		for (const config of this.starConfigs) {
			for (const activity of config.activities) {
				if (this.isActivityInvalid(activity.uid, activity.lessonId)) {
					return true;
				}
			}
		}
		return false;
	}

	cancelConfig() {
		this.configCancelled.emit();
	}

	expandedActivityIndex: number | null = null;

	toggleExpanded(index: number): void {
	this.expandedActivityIndex = this.expandedActivityIndex === index ? null : index;
	}
}
